/**
 * Time unit constants in milliseconds. Pure leaf (zero imports) so sibling const/config-*.ts leaves can reference
 * TIME_UNITS without triggering the `export *` TDZ cycle that occurs when importing from the barrel `@federa/const`.
 */

const SECOND_MS = 1000;
const MINUTE_MS = 60_000;
const HOUR_MS = 3600 * 1000;
const DAY_MS = 86400 * 1000;

const SECOND_S = 1;
const MINUTE_S = 60;
const HOUR_S = 3600;
const DAY_S = 86400;

export const TIME_UNITS = {
	SECOND_MS,
	MINUTE_MS,
	HOUR_MS,
	DAY_MS,
	WEEK_MS: 7 * DAY_MS,
	FORTNIGHT_MS: 14 * DAY_MS,
	MONTH_MS: 30 * DAY_MS,
	QUARTER_MS: 90 * DAY_MS,
	YEAR_MS: 365 * DAY_MS,
	SECOND_S,
	MINUTE_S,
	HOUR_S,
	DAY_S,
	WEEK_S: 7 * DAY_S,
	YEAR_S: 365 * DAY_S,
} as const;

/**
 * The CURRENT time as unix EPOCH SECONDS -- `Date.now()` floored through {@link TIME_UNITS}`.SECOND_MS`, as ONE
 * decision rather than a division each caller re-assembles.
 *
 * FLOOR, NOT ROUND, and that is the whole reason this is a named member: `Math.round` would hand back a second that
 * has not started yet, so a freshly minted `iat` could sit in the FUTURE of a verifier that floors -- and every
 * consumer here is a token clock, where a half-second of future-dating is a fail-closed rejection of a valid token.
 * The floored spelling was already unanimous across all four call sites; naming it is what keeps a fifth from
 * arriving rounded.
 *
 * Minted 2026-08-23: FOUR leaves spelled `Math.floor(Date.now() / TIME_UNITS.SECOND_MS)` inline -- both signed-session
 * signers (`utils/security/app-session-token.ts`, `utils/security/guest-session-token.ts`), the shared expiry
 * predicate they both verify through (`isUnexpiredEpochSeconds`, `const/signed-token-codec.ts`) and the JWT registered
 * -claim validator (`utils/auth/jwt-verify-core.ts`). The signer/verifier pair is the load-bearing one: those two
 * MUST read the same clock in the same unit, and the only thing that held them together was that two files happened
 * to spell the same expression.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed beside the `SECOND_MS` divisor it reads, on the const floor,
 * so pure `utils/` leaves can value-import it -- `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bans a utils-to-utils value
 * edge with no allowlist, which is exactly what mandated a private copy in each consumer.
 */
export function nowEpochSeconds(): number {
	return Math.floor(Date.now() / SECOND_MS);
}

/** The whole seconds elapsed since `startMs`, rounded to nearest -- the run-duration figure a wrapper's summary line and
 *  the property enrichment and image steps each spelled as `Math.round((Date.now() - start) / SECOND_MS)`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): homed beside the `SECOND_MS` divisor it reads, on the const floor. */
export function elapsedWholeSecondsSince(startMs: number): number {
	return Math.round((Date.now() - startMs) / SECOND_MS);
}

/** The milliseconds since `instantMs` on an INJECTED clock -- a wait's elapsed time, a lock's age, a count's spent
 *  budget -- the subtraction the clock-seamed hook cores (`utils/hooks/commit-lane-lock-core.ts`,
 *  `utils/hooks/rule-rekey-save-gate-core.ts`) each spelled over their own `nowMs`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): on the const floor, so sibling `utils/` cores may value-import it. */
export function msSinceOn(clock: { readonly nowMs: () => number }, instantMs: number): number {
	return clock.nowMs() - instantMs;
}

/** The milliseconds since `startedMs` on the SYSTEM clock: the run, step, shard and wait durations every host stamped
 *  as `Date.now() - startedAtMs` (thirty-three leaves at the 2026-09-27 dedup reading), now one spelling.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): {@link msSinceOn} over the system clock, on the const floor. */
export function msSince(startedMs: number): number {
	return msSinceOn({ nowMs: Date.now }, startedMs);
}

/** The milliseconds from `from` to `to`, negative once `to` is the earlier instant -- the auction countdown, the listing's
 *  urgency text and the toolchain release lag each spelled as `to.getTime() - from.getTime()` (2026-09-28 fold).
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the two-instant twin of {@link msSince}, on the same floor. */
export function msBetween(from: Date, to: Date): number {
	return to.getTime() - from.getTime();
}

/** America/New_York UTC-offset hours: 4 during EDT, 5 during EST. Single source for converting a tz-naive Eastern
 * wall-clock time to UTC after an `isDST(...)` check. */
export const EASTERN_UTC_OFFSET_HOURS = { DST: 4, STANDARD: 5 } as const;

/** Numeric threshold (ms) above which a numeric value is treated as a Unix epoch in milliseconds rather than seconds.
 * Equals 10^12 (~Sep 2001). Use when interpreting raw numeric timestamps where both are possible. */
export const EPOCH_MS_THRESHOLD = 1_000_000_000_000;

/** Plausible upper bound (ms) for Unix timestamps: January 1, 2100 UTC. Paired with `EPOCH_MS_THRESHOLD` to
 * bracket "reasonable" raw numeric timestamps when distinguishing them from arbitrary numeric strings. */
export const EPOCH_MS_UPPER_BOUND = 4_102_444_800_000;

/**
 * The BOUNDED plausibility bracket -- above {@link EPOCH_MS_THRESHOLD} and below {@link EPOCH_MS_UPPER_BOUND} -- as one
 * decision rather than a comparison pair each caller re-assembles. Its live consumer is `parseDate`
 * (`const/date-parse-format.ts`), discriminating a numeric string from a date string, and
 * `RUN_OR_STEP_ID_TOKEN_RE_SOURCE` (`const/run-id-shape.ts`) records this bracket as the numeric guard any run-id
 * matcher must pair with the width test, because a regex cannot express a range and a width-only classifier is exactly
 * what once dated `run-9999999999999-x` to nothing.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed beside the two bounds it reads, on the const floor, so pure
 * `utils/` leaves can value-import it under `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` -- which is what made the copies.
 *
 * RECORDED GAP, and the reason no forbid-rule ships with this extraction: the THRESHOLD-ONLY comparison is a DIFFERENT
 * predicate, not an unadopted spelling of this one. `parseDateToEpochMs` (`utils/dates/epoch-ms.ts`) and `formatSafeWith`
 * (`const/date-format.ts`) each test `> EPOCH_MS_THRESHOLD` with NO upper bound, so adopting this bracket there
 * would newly reject any value past 2100 and change what those two parsers accept -- a behaviour change, never a
 * mechanical adoption, and a rule prescribing it would be prescribing a wrong cure. Those two stay uncovered by any
 * matcher until someone adjudicates the upper-bound question per site.
 */
export function isPlausibleEpochMs(ts: number): boolean {
	return ts > EPOCH_MS_THRESHOLD && ts < EPOCH_MS_UPPER_BOUND;
}

// TRAILING WINDOWS

/**
 * The instant a trailing window opens: `windowHours` back from the `nowMs` it is read at -- ONE decision rather than
 * the subtraction each census, query plan or journal read re-assembles over {@link TIME_UNITS}`.HOUR_MS`.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed beside the `HOUR_MS` factor it reads, on the const floor, so
 * every census leaf, flow host, query plan and tool binding can value-import it.
 */
export const trailingWindowStartMs = (nowMs: number, windowHours: number): number => nowMs - windowHours * HOUR_MS;

/**
 * The whole-minute count of a millisecond span, rounded to nearest, as the text a human-read sentence carries --
 * ONE decision rather than the `String(Math.round(ms / 60_000))` each verdict sentence re-assembles. Minted
 * 2026-09-23 off two copies: the host-process classifier's private `minutes` helper
 * (`const/host-process-ownership-classify.ts`) and the same expression inline in the child arm
 * (`const/host-process-child-ownership.ts`).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed beside the `MINUTE_MS` divisor it reads, on the const floor,
 * so pure `utils/` leaves can value-import it under `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`.
 */
export function roundedMinutesLabel(ms: number): string {
	return String(Math.round(ms / MINUTE_MS));
}

/**
 * The whole minute an epoch-millisecond instant falls in, floored -- the bucket two instants share when they happened
 * in the same minute. Homed here 2026-09-23 off `const/dispatch-account-cells.ts`, deleted with the dispatch-account
 * reconciliation that minted it; the board's flow-run fold buckets its clean starts by it
 * (`spa/mission/server/flow-run-fold.ts`).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed beside the `MINUTE_MS` divisor it reads, on the const floor.
 */
export function minuteOf(atMs: number): number {
	return Math.floor(atMs / MINUTE_MS);
}
